// === IMPORTS ===
import React, { useState, useRef } from 'react';
import { Upload, Link as LinkIcon, Image as ImageIcon, RotateCcw, Check, Sparkles } from 'lucide-react';
import '../styles/twin-icons-shared.css';
import './CustomAssetUploader.css';

interface CustomAssetUploaderProps {
  title?: string;
  label?: string;
  subtitle?: string;
  currentImage?: string;
  onImageChange?: (imageUri: string | undefined) => void;
  onImageUploaded?: (imageUri: string | undefined) => void;
  defaultIcon?: string;
  compact?: boolean;
  assetType?: string;
}

// === COMPONENTE: SUBIDOR DE IMÁGENES / TEXTURAS PERSONALIZADAS ===
export const CustomAssetUploader: React.FC<CustomAssetUploaderProps> = ({
  title,
  label,
  subtitle,
  currentImage,
  onImageChange,
  onImageUploaded,
  defaultIcon = '📦',
  compact = false,
  assetType,
}) => {
  const displayTitle = title || label || 'Upload Image / Texture';
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const notifyChange = (uri: string | undefined) => {
    if (onImageChange) onImageChange(uri);
    if (onImageUploaded) onImageUploaded(uri);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must not exceed 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        notifyChange(result);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleApplyUrl = (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlInput.trim()) return;
    notifyChange(urlInput.trim());
    setUrlInput('');
    setShowUrlInput(false);
  };

  // === RENDER ===
  return (
    <div className="twin-uploader">
      {/* === CABECERA: TÍTULO Y ESTADO === */}
      <div className="twin-uploader-header">
        <div className="twin-uploader-title">
          <ImageIcon className="twin-icon-sm" />
          <span>{displayTitle}</span>
        </div>
        {currentImage && (
          <span className="twin-uploader-badge">
            <Check className="twin-icon-xs" /> Custom
          </span>
        )}
      </div>

      {subtitle && <p className="twin-uploader-subtitle">{subtitle}</p>}

      {/* === CAJA DE VISTA PREVIA === */}
      <div className="twin-uploader-preview-box">
        <div className="twin-uploader-preview-thumb">
          {currentImage ? (
            <img
              src={currentImage}
              alt="Custom asset preview"
              className="twin-uploader-preview-img"
            />
          ) : (
            <span className="twin-uploader-default-icon">{defaultIcon}</span>
          )}
        </div>

        <div className="twin-uploader-preview-text">
          <p className="twin-uploader-preview-status">
            {currentImage ? 'Active custom graphic' : 'Using default graphic'}
          </p>
          <p className="twin-uploader-preview-hint">
            {currentImage ? 'PNG, JPG, SVG or WebP loaded' : 'You can upload your own image to replace this icon'}
          </p>
        </div>
      </div>

      {/* === BOTONES DE ACCIÓN === */}
      <div className="twin-uploader-actions">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          className="twin-hidden"
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="twin-uploader-btn twin-uploader-btn--primary"
        >
          <Upload className="twin-icon-sm" /> Upload Image
        </button>

        <button
          type="button"
          onClick={() => setShowUrlInput(!showUrlInput)}
          className="twin-uploader-btn twin-uploader-btn--secondary"
          title="Use image URL"
        >
          <LinkIcon className="twin-icon-sm" /> URL
        </button>

        {currentImage && (
          <button
            type="button"
            onClick={() => notifyChange(undefined)}
            className="twin-uploader-btn twin-uploader-btn--danger"
            title="Reset original graphic"
          >
            <RotateCcw className="twin-icon-sm" /> Reset
          </button>
        )}
      </div>

      {/* === FORMULARIO DE URL === */}
      {showUrlInput && (
        <form onSubmit={handleApplyUrl} className="twin-uploader-url-form">
          <input
            type="url"
            placeholder="https://example.com/asset.png"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            className="twin-uploader-url-input"
          />
          <button type="submit" className="twin-uploader-url-submit">
            Apply
          </button>
        </form>
      )}
    </div>
  );
};